import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  GripVertical,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  Navigation,
  MoreHorizontal,
  X
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '../../lib/utils'
import type { WorkflowStep } from '../../types/workflow'

interface StartBlockProps {
  step: WorkflowStep
  isSelected: boolean
  onSelect: () => void
  onToggle: (enabled: boolean) => void
  onDelete: () => void
  onDuplicate: () => void
  onUpdate: (updates: Partial<WorkflowStep>) => void
  className?: string
}

export function StartBlock({
  step,
  isSelected,
  onSelect,
  onToggle,
  onDelete,
  onDuplicate,
  className
}: StartBlockProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: step.id,
    data: {
      type: 'step',
      step
    }
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const currentUrl = (step.params as any).url || ''

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={cn(
        "workflow-block transition-all duration-200 cursor-pointer hover:shadow-md border-2 border-green-200 bg-green-50",
        isSelected && "ring-2 ring-primary ring-offset-2",
        !step.enabled && "opacity-60",
        isDragging && "opacity-50 rotate-2 shadow-lg",
        className
      )}
      onClick={onSelect}
    >
      <CardContent className="workflow-block-content">
        {/* Drag Handle Area - Full Left Side */}
        <div
          {...attributes}
          {...listeners}
          className="workflow-block-drag-handle"
          title="Drag to reorder"
        >
          <GripVertical className="drag-icon" />
        </div>

        {/* Main Content Area */}
        <div className="workflow-block-main">
          {/* Header Row */}
          <div className="workflow-block-header">
            <div className="workflow-block-title-row">
              <div className="workflow-block-indicators">
                <div className="category-indicator bg-green-500" />
                <Navigation className="step-icon text-green-600" />
              </div>
              <div className="workflow-block-title-content">
                <h4 className="step-title text-green-700">Start</h4>
                <div className="step-badges">
                  <Badge variant="outline" className="step-badge border-green-300 text-green-700">
                    Navigate to URL
                  </Badge>
                  {!step.enabled && (
                    <Badge variant="secondary" className="step-badge">
                      Disabled
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Content Summary */}
          <div className="workflow-block-summary">
            {currentUrl ? (
              <div className="url-summary">
                <span className="url-text">{currentUrl}</span>
              </div>
            ) : (
              <div className="config-prompt">
                <span className="prompt-text">Click to configure URL</span>
              </div>
            )}
          </div>

          {/* Controls Footer */}
          <div className="workflow-block-controls">
            <div className="controls-left">
              <div className="enable-control">
                <span className="control-label">Enabled</span>
                <Switch
                  checked={step.enabled}
                  onCheckedChange={onToggle}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            <div className="controls-right">
              <Button
                variant="ghost"
                size="sm"
                className="control-btn delete-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                title="Delete step"
              >
                <X className="h-3 w-3" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="control-btn"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDuplicate(); }}>
                    <Copy className="h-4 w-4 mr-2" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); onToggle(!step.enabled); }}
                  >
                    {step.enabled ? (
                      <>
                        <EyeOff className="h-4 w-4 mr-2" />
                        Disable
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4 mr-2" />
                        Enable
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete();
                    }}
                    className="text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
